/**
 * AbilityBridge.ts — Bridges existing ability data into combat-usable stats.
 *
 * Parses the MechanicalEffect fields from abilities.json (damage strings like
 * "1d8 cold + STR modifier", frostbiteCost numbers, tempHP values) into
 * numeric CombatAbility objects. Zero data changes needed — all 60 existing
 * abilities work automatically.
 */

import type { ClassId } from '@game/data/types';
import type { CombatAbility } from './CombatTypes';
import { getClassAbilities } from '@game/data/abilities';

// ─── Damage Parsing ─────────────────────────────────────────────

/**
 * Parse a D&D-style damage string into a flat average number.
 * Examples:
 *   "1d8 cold" → 4.5
 *   "2d6 + STR modifier" → 7
 *   "3d8 cold + STR modifier" → 13.5
 *   "1d8 cold + 1d6 fire" → 8
 *   undefined → 0
 *
 * Uses average die values (d4=2.5, d6=3.5, d8=4.5, d10=5.5, d12=6.5, d20=10.5).
 */
export function parseDamageString(damage: string | undefined): number {
  if (!damage) return 0;

  let total = 0;
  // Match patterns like "2d8" or "1d6"
  const dicePattern = /(\d+)d(\d+)/g;
  let match: RegExpExecArray | null;

  while ((match = dicePattern.exec(damage)) !== null) {
    const count = parseInt(match[1], 10);
    const sides = parseInt(match[2], 10);
    total += count * ((sides + 1) / 2);
  }

  // Match flat bonuses like "+ 5" (not "modifier")
  const flatPattern = /\+\s*(\d+)(?!\s*d)/g;
  while ((match = flatPattern.exec(damage)) !== null) {
    // Skip if this is part of a die expression (already captured)
    if (!damage.substring(0, match.index).match(/\dd$/)) {
      total += parseInt(match[1], 10);
    }
  }

  return Math.round(total);
}

// ─── Bridge ─────────────────────────────────────────────────────

/**
 * Get all combat-usable abilities for a class at their current tiers.
 * Reads from the static ability data + current tier map from GameState.
 */
export function getClassCombatAbilities(
  classId: ClassId,
  abilityTiers: Map<string, number>,
): CombatAbility[] {
  const classAbilities = getClassAbilities(classId);
  if (!classAbilities) return [];

  const result: CombatAbility[] = [];

  for (const ability of classAbilities.abilities) {
    const currentTier = abilityTiers.get(ability.id) ?? 1;
    const tierData = ability.tiers.find(t => t.tier === currentTier);
    if (!tierData) continue;

    const me = tierData.mechanicalEffect;

    result.push({
      id: ability.id,
      name: tierData.name,
      damage: parseDamageString(me.damage),
      frostbiteCost: me.frostbiteCost ?? 0,
      bonusEffect: me.additionalEffect,
      defenseBonus: me.tempHP ? 0 : (me.resistances ? 2 : 0),
      tempHP: me.tempHP ?? 0,
    });
  }

  return result;
}

/**
 * Get a single combat ability by ID for a class at its current tier.
 */
export function getCombatAbility(
  classId: ClassId,
  abilityId: string,
  abilityTiers: Map<string, number>,
): CombatAbility | undefined {
  return getClassCombatAbilities(classId, abilityTiers)
    .find(a => a.id === abilityId);
}
